import React, { useState } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormLabel,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { uniqueId } from 'lodash';
import { OwnerSelect } from '@/components/selects/OwnerSelect';
import { useBatchUpdateDealsMutation } from '@/features/deals/dealsApi';
import { useBatchUpdateTasksMutation } from '@/features/engagements/engagementsApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { showToast, hideToast } from '@/features/toast/toastSlice';
import { setSelected as setDealSelected } from '@/features/table/dealsTableSlice';
import { setSelected as setTaskSelected } from '@/features/table/tasksTableSlice';
import { getObjectType, buildBulkFailureMessage, capitalize } from '@/utils/constants';

export const BulkReassignButton = ({ objectType = 'deal', pipeline }) => {
	const dispatch = useDispatch();
	const isTask = objectType === 'task';
	const tableKey = isTask ? 'taskTable' : 'dealTable';
	const setSelected = isTask ? setTaskSelected : setDealSelected;

	const { selected } = useSelector((state) => state[tableKey]);
	const { username: email } = useSelector((state) => state.msalAccount.account);
	const isDebtorPipeline = pipeline === process.env.REACT_APP_PIPELINE_DEBTORS;
	const isClaimsPipeline = pipeline === process.env.REACT_APP_PIPELINE_CLAIMS;
	const objectLabel = isTask
		? 'task'
		: getObjectType(isDebtorPipeline ? 'debtor' : isClaimsPipeline ? 'ticket' : 'deal');

	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const [open, setOpen] = useState(false);
	const [selectedOwner, setSelectedOwner] = useState(null);

	const [batchUpdateDeals, { isLoading: isUpdatingDeals }] = useBatchUpdateDealsMutation();
	const [batchUpdateTasks, { isLoading: isUpdatingTasks }] = useBatchUpdateTasksMutation();

	const isSubmitting = isUpdatingDeals || isUpdatingTasks;

	const openDialog = () => {
		setSelectedOwner(userDetails ? { id: userDetails.userId } : null);
		setOpen(true);
	};

	const handleClose = () => {
		if (isSubmitting) return;
		setOpen(false);
		setSelectedOwner(null);
	};

	const handleConfirm = async () => {
		if (!selectedOwner || selected.length === 0 || isSubmitting) return;

		const requestedCount = selected.length;
		const selectedSnapshot = selected;
		const dealIds = selected.map((deal) => deal.dealId);
		const toastId = uniqueId('bulk-reassign-');
		const properties = {
			ownerDocId: selectedOwner.id ?? null,
		};

		dispatch(setSelected([]));
		setOpen(false);
		setSelectedOwner(null);

		dispatch(
			showToast({
				id: toastId,
				message: `Reassigning ${requestedCount} ${objectLabel}${
					requestedCount > 1 ? 's' : ''
				}…`,
				autohide: false,
				loading: true,
			}),
		);

		try {
			const res = isTask
				? await batchUpdateTasks({
						tasks: selectedSnapshot,
						properties,
					}).unwrap()
				: await batchUpdateDeals({
						dealIds,
						properties,
					}).unwrap();

			dispatch(hideToast(toastId));
			const failed = res?.failed ?? [];

			if (failed.length > 0) {
				dispatch(
					showToast({
						message: buildBulkFailureMessage(failed),
						error: true,
					}),
				);
				return;
			}

			dispatch(
				showToast({
					message: `Successfully reassigned ${requestedCount} ${objectLabel}${
						requestedCount > 1 ? 's' : ''
					}.`,
					success: true,
				}),
			);
		} catch (e) {
			console.error('Error bulk reassigning', e);
			dispatch(hideToast(toastId));
			dispatch(
				showToast({
					message: e?.data?.message ?? e?.message ?? 'Failed to reassign',
					error: true,
				}),
			);
		}
	};

	return (
		<>
			<Button
				variant='outlined'
				disabled={isSubmitting || selected.length === 0}
				sx={{ borderRadius: '15px' }}
				onClick={openDialog}
			>
				{'Change assignee'}
				{selected.length > 0 && (
					<span style={{ marginLeft: '0.25em' }}>{`(${selected.length})`}</span>
				)}
			</Button>
			<Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
				<DialogTitle>
					{`Reassign ${selected.length} ${objectLabel}${selected.length > 1 ? 's' : ''}`}
				</DialogTitle>
				<DialogContent sx={{ paddingBottom: '2em' }}>
					<FormControl fullWidth sx={{ mt: 1 }}>
						<FormLabel>{`${capitalize(objectLabel)} assignee`}</FormLabel>
						<OwnerSelect
							initialId={selectedOwner?.id}
							onChange={setSelectedOwner}
							disabled={isSubmitting}
							showGroup={true}
							MenuProps={{
								anchorOrigin: {
									vertical: 'bottom',
									horizontal: 'left',
								},
								transformOrigin: {
									vertical: 'top',
									horizontal: 'left',
								},
								marginThreshold: 0,
								PaperProps: {
									sx: { maxHeight: 280 },
								},
							}}
						/>
					</FormControl>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} disabled={isSubmitting} color='primary'>
						Cancel
					</Button>
					<Button
						color='primary'
						variant='contained'
						disabled={isSubmitting || !selectedOwner}
						onClick={handleConfirm}
					>
						Confirm
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
